import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { InstantNotificationSetting } from "@/features/user-profile/types";
import { useToast } from "@/hooks/useToast";
import { notificationApi } from "@/lib/humhub-api/notification";

export const useInstantNotificationSettings = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    data: instantNotificationSettings,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["instantNotificationSettings"],
    queryFn: () => notificationApi.getInstantNotificationSettings(),
    staleTime: 5 * 60 * 1000,
  });

  const { mutateAsync: updateSettings, isPending: isSaving } = useMutation({
    mutationFn: (settings: Record<string, InstantNotificationSetting>) =>
      notificationApi.updateInstantNotificationSettings(settings),
    onSuccess: (data) => {
      queryClient.setQueryData(["instantNotificationSettings"], data);
      setSubmitError(null);

      toast({
        title: t("common.settingsSaved"),
        description: t("common.instantNotificationSettingsSaved"),
      });
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update settings";
      setSubmitError(errorMessage);

      toast({
        title: t("common.settingsError"),
        description: errorMessage,
      });
    },
  });

  const saveSettings = async (values: Record<string, boolean>) => {
    if (!instantNotificationSettings) return;

    const payload = instantNotificationSettings.categories.reduce(
      (acc, category) => {
        const baseSetting = instantNotificationSettings.settings[
          category.id
        ] || {
          enabled: category.default_setting,
          is_fixed: category.is_fixed,
          default_setting: category.default_setting,
        };

        acc[category.id] = {
          ...baseSetting,
          enabled: values[category.id] ?? baseSetting.enabled,
        };

        return acc;
      },
      {} as Record<string, InstantNotificationSetting>,
    );

    await updateSettings(payload);
  };

  return {
    instantNotificationSettings,
    isLoading,
    isSaving,
    saveSettings,
    submitError,
    refetch,
  };
};
